import { Component, OnInit, ViewEncapsulation, Inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { isEmpty } from 'lodash';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { of, Observable, throwError } from 'rxjs';
import { catchError, finalize, first, map, switchMap } from 'rxjs/operators';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { environment } from '@env/environment';
import { VirusScannerService } from '@app/shared/services/virus-scanner.service';
import { LampCatalogueStoreService } from '@app/shared/services/store/lamp-catalogue-store.service';
import { IProductManagementDialogComponent } from '@app/product-catalog-management/models/product-management-dialog';
import { LampDetails } from '@app/shared/models/lamp-catalogue/lamp-details';
import { HttpErrorResponse } from '@angular/common/http';
import { SafeUrl } from '@angular/platform-browser';
import { LuminaireCategory } from '@app/shared/models/luminaire-category/luminaire-category';

@Component({
  selector: 'app-product-management.dialog',
  templateUrl: './product-management-dialog.component.html',
  styleUrls: ['./product-management-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProductManagementDialogComponent implements OnInit, OnDestroy {
  public filteredManufacturers?: Observable<string[]>;
  public germanBoolean: Record<string, string>[] = [
    { value: '1', text: 'Ja' },
    { value: '0', text: 'Nein' },
  ];
  public imageName?: string = '';
  public isEditMode = false;
  public isImageValid = true;
  public isNewImageUploaded = false;
  public lamp: LampDetails = {} as LampDetails;
  public luminaireCategories: LuminaireCategory[] = [];
  public manufacturerInput = new FormControl('', [Validators.required]);
  public productManagementForm: FormGroup = this.formBuilder.group({});

  private dataStore: LampDetails = {} as LampDetails;
  private isImageUploadedAfterInitialLoad = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IProductManagementDialogComponent,
    private azureBlobStorageService: AzureBlobStorageService,
    private changeDetector: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private lampCatalogueStoreService: LampCatalogueStoreService,
    private matDialogRef: MatDialogRef<ProductManagementDialogComponent>,
    private overlayLoaderService: OverlayLoaderService,
    private scannerService: VirusScannerService
  ) {}

  public ngOnInit(): void {
    this.productManagementForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      type: ['', [Validators.required]],
      watt: ['', [Validators.required]],
      chkLumen4k: [false],
      chkLumen3k: [false],
      lumen4k: [''],
      lumen3k: [''],
      optics: ['', [Validators.required]],
      price: ['', [Validators.required]],
      tiltAngle: ['', [Validators.required]],
      warranty: ['', [Validators.required]],
      controlCore: ['', [Validators.required]],
      dimming: ['', [Validators.required]],
      modelNumber: ['', [Validators.required]],
      sdMaterial: ['', [Validators.required]],
      braid: ['', [Validators.required]],
    });

    this.dataStore = { ...this.data.lampDetails };
    this.initializeLampDetails();
  }

  public ngOnDestroy(): void {
    if (!isEmpty(this.data.lampDetails)) {
      this.data.lampDetails = { ...this.dataStore };
    }
  }

  get name() {
    return this.productManagementForm.get('name');
  }

  get type() {
    return this.productManagementForm.get('type');
  }

  get watt() {
    return this.productManagementForm.get('watt');
  }

  get chkLumen4k() {
    return this.productManagementForm.get('chkLumen4k');
  }

  get chkLumen3k() {
    return this.productManagementForm.get('chkLumen3k');
  }

  get lumen4k() {
    return this.productManagementForm.get('lumen4k');
  }

  get lumen3k() {
    return this.productManagementForm.get('lumen3k');
  }

  get optics() {
    return this.productManagementForm.get('optics');
  }

  get price() {
    return this.productManagementForm.get('price');
  }

  get tiltAngle() {
    return this.productManagementForm.get('tiltAngle');
  }

  get warranty() {
    return this.productManagementForm.get('warranty');
  }

  get controlCore() {
    return this.productManagementForm.get('controlCore');
  }

  get dimming() {
    return this.productManagementForm.get('dimming');
  }

  get modelNumber() {
    return this.productManagementForm.get('modelNumber');
  }

  get sdMaterial() {
    return this.productManagementForm.get('sdMaterial');
  }

  get braid() {
    return this.productManagementForm.get('braid');
  }

  get isLumen4kValid() {
    const isLumen4kValid = this.chkLumen4k?.value && this.lumen4k?.value > 0;
    return isLumen4kValid;
  }

  get isLumen3kValid() {
    const isLumen3kValid = this.chkLumen3k?.value && this.lumen3k?.value > 0;
    return isLumen3kValid;
  }

  public setDefaultImage(): void {
    this.data.lampDetails.imageObservable = this.azureBlobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/images/lamps/DefaultLamp.png`);
  }

  public attachImage(lamp: LampDetails, files: FileList): void {
    if (files.length === 0) {
      return;
    }

    const file = files[0];
    if (!this.isFileImage(file)) {
      return;
    } else {
      this.validateImageSize(lamp, file);
    }

    if (!this.data.isEditMode && !this.isImageUploadedAfterInitialLoad) {
      this.isImageUploadedAfterInitialLoad = true;
    }
  }

  public removeImage(lamp: LampDetails): void {
    lamp.image = null;
    lamp.oldImageName = lamp.imageName;
    this.imageName = undefined;
    this.isNewImageUploaded = false;
    this.isImageValid = false;
  }

  public cancel(): void {
    this.matDialogRef.close(true);
  }

  public confirm(): void {
    const lamp = new FormData();

    if (!!this.lamp.file) {
      lamp.append('File', this.lamp.file);
    }

    lamp.append('Name', this.name?.value);
    lamp.append('LuminaireCategoryId', this.type?.value);
    lamp.append('PerformanceWatts', this.watt?.value);
    lamp.append('Optics', this.optics?.value);
    lamp.append('NetPrice', this.price?.value);
    lamp.append('TiltAngle', this.tiltAngle?.value);
    lamp.append('GuarenteeYears', this.warranty?.value);
    lamp.append('ControlWire', this.controlCore?.value);
    lamp.append('Dimming', this.dimming?.value);
    lamp.append('ModelNumber', this.modelNumber?.value);
    lamp.append('Sdmaterial', this.sdMaterial?.value);
    lamp.append('Braid', this.braid?.value);
    lamp.append('OldImageName', this.data.isEditMode && !!this.lamp.oldImageName ? this.lamp.oldImageName : this.lamp.imageName);

    if (this.chkLumen4k?.value) {
      lamp.append('LightElectricity4000K', this.lumen4k?.value);
    }
    if (this.chkLumen3k?.value) {
      lamp.append('LightElectricity3000K', this.lumen3k?.value);
    }
    lamp.append('Manufacturer', this.manufacturerInput.value ?? '');
    lamp.append('IsOnlyInSalesTool', 'true');

    this.overlayLoaderService.isLoading = true;

    this.updateProductCatalogue(lamp)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.scannerService.errorChecker(err);
          return throwError(() => err);
        }),
        switchMap(() => this.lampCatalogueStoreService.getLamps()),
        finalize(() => this.completeForm()),
        first()
      )
      .subscribe();
  }

  public isButtonDisabled(): boolean {
    if (this.productManagementForm.valid && this.manufacturerInput.valid && this.isImageValid && (this.isLumen4kValid || this.isLumen3kValid)) {
      return !this.data.isEditMode && !this.isImageUploadedAfterInitialLoad;
    }

    return true;
  }

  private isFileImage(file: File): boolean {
    return file.type.includes('image');
  }

  private validateImageSize(lamp: LampDetails, file: File): void {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      const img = new Image();
      img.src = window.URL.createObjectURL(file);

      setTimeout(() => {
        const withinSizeLimits = this.withinSizeLimits(img.naturalWidth, img.naturalHeight);
        if (withinSizeLimits) {
          lamp.oldImageName = lamp.imageName;
          lamp.oldImage = lamp.image;
          lamp.image = of(fileReader.result as SafeUrl);
          lamp.imageName = file.name;
          lamp.file = file;
          this.imageName = file.name;
          this.isNewImageUploaded = true;
          this.isImageValid = true;
        } else {
          this.removeImage(lamp);
        }

        this.changeDetector.detectChanges();
      }, 100);
    };
  }

  private initializeLampDetails(): void {
    if (!isEmpty(this.data.lampDetails)) {
      this.lamp = this.data.lampDetails;
      this.lamp.image = this.data.lampDetails.imageObservable;
      this.imageName = this.lamp.imageName;
    }

    this.filteredManufacturers = this.manufacturerInput.valueChanges.pipe(map((value) => this._filter(value ?? '')));

    this.luminaireCategories = this.data.luminaireCategories;

    if (this.data.isEditMode) {
      this.manufacturerInput.setValue(this.lamp.manufacturer);
      this.productManagementForm.get('name')?.setValue(this.lamp.name);
      this.productManagementForm.get('type')?.setValue(this.lamp.luminaireCategoryId);
      this.productManagementForm.get('watt')?.setValue(this.lamp.performanceWatts);
      if (this.lamp.lightElectricity4000K) {
        this.productManagementForm.get('chkLumen4k')?.setValue(true);
      }
      this.productManagementForm.get('lumen4k')?.setValue(this.lamp.lightElectricity4000K);
      if (this.lamp.lightElectricity3000K) {
        this.productManagementForm.get('chkLumen3k')?.setValue(true);
      }
      this.productManagementForm.get('lumen3k')?.setValue(this.lamp.lightElectricity3000K);
      this.productManagementForm.get('optics')?.setValue(this.lamp.optics);
      this.productManagementForm.get('price')?.setValue(this.lamp.netPrice);
      this.productManagementForm.get('tiltAngle')?.setValue(this.lamp.tiltAngle);
      this.productManagementForm.get('warranty')?.setValue(this.lamp.guarenteeYears);
      this.productManagementForm.get('controlCore')?.setValue(this.lamp.controlWire);
      this.productManagementForm.get('dimming')?.setValue(this.lamp.dimming);
      this.productManagementForm.get('modelNumber')?.setValue(this.lamp.modelNumber);
      this.productManagementForm.get('sdMaterial')?.setValue(this.lamp.sdmaterial);
      this.productManagementForm.get('braid')?.setValue(this.lamp.braid);
    }

    this.isImageUploadedAfterInitialLoad = false;
  }

  private withinSizeLimits(width: number, height: number): boolean {
    return width === 400 && height === 500;
  }

  private updateProductCatalogue(lampDetails: FormData): Observable<Object> {
    return this.data.isEditMode
      ? this.lampCatalogueStoreService.updateLampCatalogue(this.lamp.guid, lampDetails)
      : this.lampCatalogueStoreService.createLampEntry(lampDetails);
  }

  private completeForm(): void {
    this.overlayLoaderService.isLoading = false;
    this.cancel();
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase().trim();
    if (filterValue.length <= 0) {
      return [];
    }
    return this.data.manufacturers.filter((manufacturer: string) => manufacturer.toLowerCase().includes(filterValue));
  }
}
